



















































































































































































































































































































































































import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import dayjs from "dayjs";
import { NovaDepartment } from "@/models/CreditorModel";
import { System } from "@/models/SystemModel";
import { UserModule } from "@/store/modules/User";
import debounce from "lodash/debounce";
import trackEvent from "@/plugins/track-event";

import FormField from "@/components/AFormField.vue";
import ServiceCaseFileUpload from "./file-upload.vue";
import RegisteredCasesOverview from "./registered-cases-overview.vue";

// interfaces
import {
    Query,
    ADInoutCase,
    InoutHstAttClass,
    InoutDebtClass,
    InoutDebtorClass,
    InoutVarDatClass,
    ADInoutCaseInput,
} from "@/models/CaseModel";

interface DropFiles {
    hstId: string | null;
    file: File;
}

interface ServiceCustomer {
    customerType: null | number;
    id: string;
    firstName: string;
    lastName: string;
    companyName: null | string;
    contactPerson: null | string;
    address: string;
    coAddress: null | string;
    postalCode: string;
    city: string;
    primaryPhone: null | string;
    secondaryPhone: null | string;
    email: null | string;
}

interface Service {
    invoiceNumber: null | string; // delgivningsnummer
    typeOfDelivery: string[];
    invoiceDueDate: null | Date;
    expressDelivery: boolean;
}

interface Error {
    error: any[];
    isError: boolean;
}

@Component({
    components: {
        FormField,
        ServiceCaseFileUpload,
        RegisteredCasesOverview,
    },
})
export default class RegisterServiceCaseForm extends Vue {
    customer: ServiceCustomer = {
        customerType: null,
        id: "",
        firstName: "",
        lastName: "",
        companyName: "",
        contactPerson: "",
        address: "",
        coAddress: "",
        postalCode: "",
        city: "",
        primaryPhone: null,
        secondaryPhone: null,
        email: null,
    };
    service: Service = {
        invoiceNumber: "", // delgivningsnummer
        typeOfDelivery: ["personal"],
        invoiceDueDate: null,
        expressDelivery: false,
    };
    yourReference: string | null = null; // uppdragsnummer
    historyInfo = "";
    dropFiles: DropFiles[] = [];
    dropFilesEditDeleted: DropFiles[] = [];
    editMode = false;
    caseIdEdit: string | null = null;
    userModule = UserModule;
    novaDepartments: NovaDepartment[] = [];
    novaDepartment: NovaDepartment = {} as NovaDepartment;
    system: System | null = null;
    isLoadingRegister = false;
    isLoadingDepartments = false;
    creditorNameSearch = "";
    creditorNoSearch = "";
    errors: Error = { error: [], isError: false };
    updateRegisteredCases = false;

    async created() {
        this.isLoadingRegister = true;
        const aktivDelgivningSystem = this.getSystem("AD");
        this.system = aktivDelgivningSystem;
        await this.fetchNovaDepartments(aktivDelgivningSystem);
        this.isLoadingRegister = false;
    }

    @Prop(Object)
    query!: Query;

    get customerTypes() {
        return [
            { name: this.$t("registerCases.private") as string, value: 1 },
            { name: this.$t("registerCases.firm") as string, value: 2 },
            { name: this.$t("registerCases.privateFirm") as string, value: 3 },
        ];
    }

    get deliveryTypes() {
        return [
            { name: this.$t("service.serviceDeliveryTypeSurrogate") as string, value: "surrogate" },
            { name: this.$t("service.serviceDeliveryTypeNail") as string, value: "nail" },
            { name: this.$t("service.serviceDeliveryTypeDrop") as string, value: "drop" },
        ];
    }

    get dueDateMin() {
        // End-date must be set to a future date and only available to set for 7 or days more.
        return dayjs().add(7, "day").toDate();
    }

    get filteredDepartments() {
        return this.novaDepartments.filter(
            dep => dep.creditorNo.toString() + "," + dep.departmentNo.toString() + ": " + dep.creditorName.toLowerCase()
        );
    }

    getSystem(systemName: string) {
        return this.userModule.systems.filter(s => s.systemName === systemName)[0];
    }

    async fetchNovaDepartments(system: System) {
        this.isLoadingDepartments = true;
        try {
            const { data } = await this.$api.graphql({
                query: `query($first: Int, $where: [WhereExpressionGraph]) {
                        novaDepartments(first: $first, where: $where) {
                            items {
                                id
                                novaCreditorDataId
                                collector
                                countryCode
                                interestCode
                                interestRate
                                systemId
                                creditorName
                                creditorNo
                                departmentNo
                                description
                                termsOfpayment
                                termsOfInterest
                                novaCreditor {
                                    creditorName
                                    creditorNo
                                    departmentNo
                                    creditorDescription
                                    cType
                                }
                            }
                        }
                    }`,
                variables: {
                    first: 999,
                    where: [
                        { path: "systemId", value: system.systemId.toString() },
                        { path: "departmentNo", value: "0", negate: true },
                        ...(this.creditorNameSearch
                            ? [{ path: "creditorName", comparison: "contains", value: this.creditorNameSearch }]
                            : []),
                        ...(this.creditorNoSearch ? [{ path: "creditorNo", value: this.creditorNoSearch }] : []),
                        {
                            groupedExpressions: [
                                {
                                    path: "creditorNo",
                                    comparison: "greaterThanOrEqual",
                                    value: "7000",
                                    negate: true,
                                    connector: "or",
                                },
                                { path: "creditorNo", comparison: "lessThan", value: "8000", negate: true },
                            ],
                        },
                        {
                            groupedExpressions: [
                                {
                                    path: "creditorNo",
                                    comparison: "greaterThanOrEqual",
                                    value: "9000",
                                    negate: true,
                                    connector: "or",
                                },
                                { path: "creditorNo", comparison: "lessThan", value: "10000", negate: true },
                            ],
                        },
                    ],
                },
            });

            this.novaDepartments = data.novaDepartments.items;
            this.isLoadingDepartments = false;
        } catch (error) {
            this.isLoadingDepartments = false;
            this.$notifications.error({ message: this.$t("registerCases.fetchDepartmentsFail") as string });
            this.errors.error.push(error);
            this.errors.isError = true;
            this.$apm.captureError(error as any);
        }
    }

    async mapToWeb(caseData: ADInoutCase): Promise<void> {
        // when a user edits a case, this function populates all fields
        this.isLoadingRegister = true;
        this.editMode = true;

        this.system = this.getSystem(caseData.site);
        await this.fetchNovaDepartments(this.system);
        const department = this.novaDepartments.find(item => item.id === caseData.reggedCwcreditorId);
        this.novaDepartment = department ? department : ({} as NovaDepartment);

        this.yourReference = caseData.refNr; // uppdragsnummer
        this.historyInfo = caseData.historik;
        this.caseIdEdit = caseData.caseId;

        const index = 0;

        this.customer.customerType = caseData.inoutDebtor[index].extTyp;
        this.customer.id = caseData.inoutDebtor[index].idNr;
        this.customer.firstName = caseData.inoutDebtor[index].fnamn;
        this.customer.lastName = caseData.inoutDebtor[index].enamn;
        this.customer.address = caseData.inoutDebtor[index].adr2;
        this.customer.postalCode = caseData.inoutDebtor[index].postNr;
        this.customer.city = caseData.inoutDebtor[index].postOrt;
        this.customer.coAddress = caseData.inoutDebtor[index].extTyp === 1 ? caseData.inoutDebtor[index].adr1 : "";
        this.customer.primaryPhone = caseData.inoutDebtor[index].telefon;
        this.customer.secondaryPhone = caseData.inoutDebtor[index].teleovr;
        this.customer.email = caseData.inoutDebtor[index].email;
        this.service.expressDelivery = caseData.inoutVarDat[index].data?.toLowerCase?.() === "true"; // assign boolean value
        this.service.typeOfDelivery = caseData.inoutHist[index]?.text?.split(",");

        // firma and private firma fields
        this.customer.companyName = caseData.inoutDebtor[index].foretagNamn;
        this.customer.contactPerson =
            caseData.inoutDebtor[index].extTyp !== 1 ? caseData.inoutDebtor[index].adr1 : null;

        // service info
        this.service.invoiceNumber = caseData.inoutDebt[index].kundNr; // delgivningsnummer
        this.service.invoiceDueDate = new Date(caseData.inoutDebt[index].forfDag);

        const dropFiles: DropFiles[] = caseData.inoutHstAtt.map(file => {
            return {
                hstId: file.hstAttId,
                file: new File(["edit"], file.fileName, { type: file.fileExtension }),
            } as unknown as DropFiles;
        });

        this.dropFiles = dropFiles;
        this.isLoadingRegister = false;
    }

    onCreditorNameSearchInput = debounce((value: string) => this.updateCreditorName(value), 600);
    onCreditorNoSearchInput = debounce((value: string) => this.updateCreditorNo(value), 600);

    updateCreditorName(value: string) {
        this.creditorNameSearch = value;
        this.fetchNovaDepartments(this.system!);
    }

    updateCreditorNo(value: string) {
        this.creditorNoSearch = value;
        this.fetchNovaDepartments(this.system!);
    }

    onDateChange(date: Date) {
        const diff = dayjs(date).diff(this.dueDateMin, "day");

        this.service.expressDelivery = false;

        if (!diff) {
            this.service.expressDelivery = true;
        }
    }

    setDueDate(selectedExpressService: boolean) {
        if (selectedExpressService) {
            this.service.invoiceDueDate = this.dueDateMin;
        }
    }

    clearForm() {
        this.$buefy.dialog.confirm({
            title: this.$t("registerCases.clearAll") as string,
            message: this.$t("registerCases.clearAllMessage") as string,
            cancelText: this.$t("registerCases.cancel") as string,
            confirmText: "Ok",
            type: "is-success",
            onConfirm: () => {
                this.clearAllFields();
                this.$buefy.toast.open(this.$t("registerCases.clearAllSuccess") as string);
            },
        });
    }

    setFiles(files: []) {
        this.dropFiles = files;
    }

    setDeletedFiles(files: []) {
        this.dropFilesEditDeleted = files;
    }

    clearAllFields() {
        this.customer = {
            customerType: null,
            id: "",
            firstName: "",
            lastName: "",
            companyName: "",
            contactPerson: "",
            address: "",
            coAddress: "",
            postalCode: "",
            city: "",
            primaryPhone: null,
            secondaryPhone: null,
            email: null,
        };
        this.service = {
            invoiceNumber: "",
            typeOfDelivery: ["personal"],
            invoiceDueDate: null,
            expressDelivery: false,
        };
        this.historyInfo = "";
        this.dropFiles = [];
        this.dropFilesEditDeleted = [];
        this.yourReference = "";

        this.editMode = false;

        this.novaDepartment = {} as NovaDepartment;

        this.$nextTick(() => {
            this.updateRegisteredCases = false;
        });

        // reset form validation
        const observer: any = this.$refs.observer;
        observer.reset();
    }

    async onSave() {
        this.isLoadingRegister = true;
        try {
            const { data } = await this.$api.graphql({
                query: `mutation($newcase: InOutCaseInput!, $caseId: String) {
                        addCase(newcase: $newcase, caseId: $caseId) {
                            success
                        }
                    }`,
                variables: {
                    newcase: await this.mapToInoutCaseInput(),
                    caseId: this.editMode ? this.caseIdEdit : null,
                },
            });

            if (this.editMode) {
                this.$notifications.success({ message: this.$t("registerCases.editCaseSuccess") as string });
            } else {
                this.$notifications.success({ message: this.$t("registerCases.createCaseSuccess") as string });
            }

            if (this.dropFiles.length > 0) {
                await this.saveFiles(data.addCase[0].success);
            }

            trackEvent("register", "newcase", data.addCase[0].success);

            // refetch registered cases
            this.updateRegisteredCases = true;

            this.clearAllFields();
            this.isLoadingRegister = false;
        } catch (error) {
            this.isLoadingRegister = false;
            if (this.editMode) {
                this.$notifications.error({ message: this.$t("registerCases.editCaseFail") as string });
            } else {
                this.$notifications.error({ message: this.$t("registerCases.createCaseFail") as string });
            }
            this.$apm.captureError(error as any);
        }
    }

    formatDate(date: Date) {
        return dayjs(date).format("YYYY-MM-DD");
    }

    async mapToInoutCaseInput() {
        const newcase: ADInoutCaseInput = {} as ADInoutCaseInput;

        newcase.site = this.system!.systemName;
        newcase.departmentNo = this.novaDepartment.departmentNo;
        newcase.referenceNo = this.yourReference!;
        newcase.caseId = "1"; // gets overwritten with a GUID value in API
        newcase.creditorNo = this.novaDepartment.creditorNo;
        newcase.historyInfo = this.historyInfo;
        newcase.reggedCWCreditorId = this.novaDepartment.id;
        newcase.reggedCWCreditorName = this.novaDepartment.creditorName;
        newcase.reggedBy = UserModule.fullName;
        newcase.reggedAt = new Date();

        newcase.inoutDebtor = [
            {
                idNo: this.customer.id,
                firstName: this.customer.firstName,
                lastName: this.customer.lastName,
                companyName: this.customer.companyName,
                debtorId: 1, // gets overwritten with a GUID value in API
                extType: this.customer.customerType,
                addr2: this.customer.address,
                cityCode: this.customer.postalCode,
                city: this.customer.city,
                addr1: this.customer.contactPerson ? this.customer.contactPerson : this.customer.coAddress,
                phoneHome: this.customer.primaryPhone,
                phoneCell: this.customer.secondaryPhone,
                email: this.customer.email,
            } as unknown as InoutDebtorClass,
        ];

        newcase.inoutDebt = [
            {
                dueDay: this.formatDate(this.service.invoiceDueDate ? this.service.invoiceDueDate : new Date()),
                debtId: 1, // gets overwritten with a GUID value in API
                customerNo: this.service.invoiceNumber,
                invoiceDate: this.formatDate(this.service.invoiceDueDate ? this.service.invoiceDueDate : new Date()),
            } as unknown as InoutDebtClass,
        ];

        newcase.inoutVarDat = [
            {
                namn: "EXPRESSDELGIVNING",
                data: String(this.service.expressDelivery),
            } as unknown as InoutVarDatClass,
        ];

        newcase.inoutHist = [String(this.service.typeOfDelivery)];

        if (this.editMode && this.dropFilesEditDeleted.length > 0) {
            // These files will get deleted in db, the actual uploads are done in function: 'saveFiles'
            const hstAtts: InoutHstAttClass[] = this.dropFilesEditDeleted.map(dropFile => {
                const hstatt = {
                    attachmentId: dropFile.hstId,
                    fileName: dropFile.file.name,
                    fileExtension: dropFile.file.type,
                    file: "editDelete",
                } as unknown as InoutHstAttClass;

                return hstatt;
            });
            newcase.inoutHstAtt = hstAtts;
        }

        return newcase;
    }

    async saveFiles(caseId: string) {
        this.dropFiles = this.dropFiles.filter(dropFile => dropFile.file.size < 10000001); // remove files larger than 10MB
        this.dropFiles = this.dropFiles.filter(dropFile => !dropFile.hstId); // remove already downloaded files

        if (this.dropFiles.length > 0) {
            try {
                const data = new FormData();
                data.append("caseId", caseId);

                this.dropFiles.forEach(dropFile => {
                    data.append("files", dropFile.file);
                });

                await this.$api.post("file/inoutcaseattachments", data, {
                    headers: {
                        "Content-Type": "multipart/form-data; boundary=something",
                        Authorization: "Bearer " + UserModule.token,
                    },
                });

                this.$notifications.success({ message: this.$t("registerCases.attachFilesSuccess") as string });
            } catch (error) {
                this.$notifications.error({ message: this.$t("registerCases.attachFilesFail") as string });
                throw error;
            }
        }
    }
}
